import React, { useState, useEffect } from 'react';
import {
  FileText, Plus, Search, CheckCircle, Clock, XCircle, Eye, Check, X,
  Trash2, AlertCircle, TrendingUp, IndianRupee, ShieldCheck
} from 'lucide-react';
import { dbHelpers } from '../../config/database';
import { InvoiceData, InvoiceItem, InvoiceLetterheadModal } from './InvoiceLetterheadModal';

interface Props {
  currentUser: any;
}

const COMMON_SERVICES = [
  'Custom Web Application Development',
  'Mobile App Development (iOS & Android)',
  'Cloud Infrastructure & DevOps Setup',
  'UI/UX Design & Product Prototyping',
  'API Integration & Backend Architecture',
  'Monthly IT Maintenance & Support',
  'Software Quality Assurance & Testing'
];

export const BillingManager: React.FC<Props> = ({ currentUser }) => {
  const [invoices, setInvoices] = useState<InvoiceData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending_approval' | 'approved' | 'rejected'>('all');

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceData | null>(null);
  const [showLetterheadModal, setShowLetterheadModal] = useState<boolean>(false);
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);

  // Form State
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_address: '',
    customer_gstin: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 15 * 86400000).toISOString().split('T')[0],
    invoice_number: '',
    tax_type: 'cgst_sgst_18',
    tax_rate: 18,
    discount: 0,
    notes: '1. Payment due within 15 days from date of invoice.\n2. In case of delayed payment, 1.5% interest per month will be applicable.\n3. Make all cheques/payments payable to X2 Infotech Consultancy IT Solution Pvt. Ltd.',
    direct_approve: false
  });

  const [formItems, setFormItems] = useState<InvoiceItem[]>([
    { description: 'Custom Web Application Development - Sprint Deliverable', sac_code: '998314', qty: 1, rate: 50000, amount: 50000 }
  ]);

  const isAdmin = currentUser?.role === 'admin' || Number(currentUser?.role_id) === 1;

  // Load Invoices
  const loadInvoices = async () => {
    setLoading(true);
    try {
      const res = await dbHelpers.getInvoices();
      if (res.status && Array.isArray(res.data)) {
        setInvoices(res.data);
      } else {
        setInvoices([]);
      }
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvoices();
  }, []);

  // Form Calculations
  const subtotal = formItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  const taxableAmount = Math.max(0, subtotal - (Number(formData.discount) || 0));
  const taxAmount = (taxableAmount * (Number(formData.tax_rate) || 0)) / 100;
  const grandTotal = taxableAmount + taxAmount;

  // Handle Item row updates
  const handleItemChange = (index: number, field: keyof InvoiceItem, value: any) => {
    setFormItems(prev => {
      const updated = [...prev];
      const item = { ...updated[index], [field]: value };
      if (field === 'qty' || field === 'rate') {
        const qty = field === 'qty' ? Number(value) : item.qty;
        const rate = field === 'rate' ? Number(value) : item.rate;
        item.amount = Math.round(qty * rate * 100) / 100;
      }
      updated[index] = item;
      return updated;
    });
  };

  const addItemRow = () => {
    setFormItems(prev => [
      ...prev,
      { description: '', sac_code: '998314', qty: 1, rate: 0, amount: 0 }
    ]);
  };

  const removeItemRow = (index: number) => {
    if (formItems.length === 1) {
      alert('Invoice must have at least one service item.');
      return;
    }
    setFormItems(prev => prev.filter((_, i) => i !== index));
  };

  // Submit New Bill
  const handleSubmitInvoice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.customer_name.trim()) {
      alert('Please enter customer name.');
      return;
    }
    if (formItems.some(i => !i.description.trim() || i.amount <= 0)) {
      alert('Please provide valid description and amount for all services.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        customer_name: formData.customer_name.trim(),
        customer_email: formData.customer_email.trim(),
        customer_phone: formData.customer_phone.trim(),
        customer_address: formData.customer_address.trim(),
        customer_gstin: formData.customer_gstin.trim(),
        invoice_date: formData.invoice_date,
        due_date: formData.due_date,
        invoice_number: formData.invoice_number.trim(),
        items: formItems,
        tax_type: formData.tax_type,
        tax_rate: Number(formData.tax_rate),
        discount: Number(formData.discount),
        notes: formData.notes.trim(),
        status: (isAdmin && formData.direct_approve) ? 'approved' : 'pending_approval'
      };

      const res = await dbHelpers.createInvoice(payload);
      if (res.status) {
        alert(res.message || 'Bill created successfully!');
        setShowCreateModal(false);
        resetForm();
        loadInvoices();
      } else {
        alert(res.message || 'Failed to create invoice.');
      }
    } catch (err: any) {
      console.error('Error creating invoice:', err);
      alert(err.message || 'Server error creating invoice.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      customer_address: '',
      customer_gstin: '',
      invoice_date: new Date().toISOString().split('T')[0],
      due_date: new Date(Date.now() + 15 * 86400000).toISOString().split('T')[0],
      invoice_number: '',
      tax_type: 'cgst_sgst_18',
      tax_rate: 18,
      discount: 0,
      notes: '1. Payment due within 15 days from date of invoice.\n2. In case of delayed payment, 1.5% interest per month will be applicable.\n3. Make all cheques/payments payable to X2 Infotech Consultancy IT Solution Pvt. Ltd.',
      direct_approve: false
    });
    setFormItems([
      { description: 'Custom Web Application Development - Sprint Deliverable', sac_code: '998314', qty: 1, rate: 50000, amount: 50000 }
    ]);
  };

  // Approval Handlers
  const handleApprove = async (id: number) => {
    if (!window.confirm('Are you sure you want to approve and finalize this bill? Once approved, the official letterhead bill can be issued and printed.')) {
      return;
    }
    try {
      const res = await dbHelpers.approveInvoice(id);
      if (res.status) {
        alert('Invoice approved successfully!');
        loadInvoices();
        if (selectedInvoice && selectedInvoice.id === id) {
          setSelectedInvoice(prev => prev ? { ...prev, status: 'approved', approved_by_name: currentUser?.name || 'Admin' } : null);
        }
      } else {
        alert(res.message || 'Approval failed.');
      }
    } catch (err: any) {
      alert(err.message || 'Error approving invoice.');
    }
  };

  const handleReject = async () => {
    if (!rejectingId) return;
    try {
      const res = await dbHelpers.rejectInvoice(rejectingId, rejectionReason || 'Rejected by Admin');
      if (res.status) {
        alert('Invoice rejected.');
        setRejectingId(null);
        setRejectionReason('');
        loadInvoices();
      } else {
        alert(res.message || 'Rejection failed.');
      }
    } catch (err: any) {
      alert(err.message || 'Error rejecting invoice.');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to permanently delete this bill record?')) return;
    try {
      const res = await dbHelpers.deleteInvoice(id);
      if (res.status) {
        alert('Invoice deleted.');
        loadInvoices();
      } else {
        alert(res.message || 'Failed to delete invoice.');
      }
    } catch (err: any) {
      alert(err.message || 'Error deleting invoice.');
    }
  };

  // Filtered Invoices
  const filteredInvoices = invoices.filter(inv => {
    const matchesSearch =
      inv.invoice_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inv.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inv.customer_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inv.customer_phone?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === 'all' ? true : inv.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // KPI Metrics
  const totalBilled = invoices
    .filter(i => i.status === 'approved')
    .reduce((sum, i) => sum + Number(i.total_amount || 0), 0);

  const pendingCount = invoices.filter(i => i.status === 'pending_approval').length;
  const approvedCount = invoices.filter(i => i.status === 'approved').length;

  return (
    <div className="space-y-6">
      {/* Header & Title */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2.5">
            <FileText className="w-7 h-7 text-emerald-600" />
            Billing & Invoicing
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Generate and manage official customer tax bills matching X2 InfoTech's registered letterhead format.
          </p>
        </div>

        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm px-5 py-2.5 rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-emerald-600/20 transition-all hover:scale-[1.02]"
        >
          <Plus className="w-5 h-5" />
          Create New Bill
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Total Invoices</p>
            <h3 className="text-2xl font-black text-slate-900 mt-1">{invoices.length}</h3>
          </div>
          <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center">
            <FileText className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-amber-500 uppercase tracking-wider">Pending Approval</p>
            <h3 className="text-2xl font-black text-slate-900 mt-1">{pendingCount}</h3>
          </div>
          <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center">
            <Clock className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-emerald-600 uppercase tracking-wider">Approved Bills</p>
            <h3 className="text-2xl font-black text-slate-900 mt-1">{approvedCount}</h3>
          </div>
          <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
            <CheckCircle className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Approved Revenue</p>
            <h3 className="text-2xl font-black text-[#15803d] mt-1">
              ₹{totalBilled.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            </h3>
          </div>
          <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center">
            <IndianRupee className="w-6 h-6" />
          </div>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
        {/* Search */}
        <div className="relative w-full md:w-80">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
          <input
            type="text"
            placeholder="Search by customer or bill #..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-xs rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
        </div>

        {/* Status Filter Tabs */}
        <div className="flex items-center gap-1.5 p-1 bg-slate-100 rounded-xl w-full md:w-auto overflow-x-auto text-xs font-semibold">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-3 py-1.5 rounded-lg transition-all ${
              statusFilter === 'all' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            All ({invoices.length})
          </button>
          <button
            onClick={() => setStatusFilter('pending_approval')}
            className={`px-3 py-1.5 rounded-lg transition-all ${
              statusFilter === 'pending_approval' ? 'bg-white text-amber-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Pending ({pendingCount})
          </button>
          <button
            onClick={() => setStatusFilter('approved')}
            className={`px-3 py-1.5 rounded-lg transition-all ${
              statusFilter === 'approved' ? 'bg-white text-emerald-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Approved ({approvedCount})
          </button>
          <button
            onClick={() => setStatusFilter('rejected')}
            className={`px-3 py-1.5 rounded-lg transition-all ${
              statusFilter === 'rejected' ? 'bg-white text-red-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Rejected
          </button>
        </div>
      </div>

      {/* Invoices List Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-slate-400 flex flex-col items-center justify-center gap-2">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            <p className="text-sm">Loading billing records...</p>
          </div>
        ) : filteredInvoices.length === 0 ? (
          <div className="py-16 text-center text-slate-400">
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-30 text-emerald-600" />
            <h4 className="font-bold text-slate-700">No Invoices Found</h4>
            <p className="text-xs text-slate-400 mt-1 max-w-sm mx-auto">
              {searchQuery ? 'No invoices match your search criteria.' : 'Create a new customer bill to get started with the official letterhead format.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase text-[10px] tracking-wider">
                <tr>
                  <th className="py-3 px-4">Invoice #</th>
                  <th className="py-3 px-4">Customer Details</th>
                  <th className="py-3 px-4">Date</th>
                  <th className="py-3 px-4">Services</th>
                  <th className="py-3 px-4 text-right">Tax (₹)</th>
                  <th className="py-3 px-4 text-right">Grand Total (₹)</th>
                  <th className="py-3 px-4 text-center">Status</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredInvoices.map((inv) => {
                  const isApproved = inv.status === 'approved';
                  const isRejected = inv.status === 'rejected';

                  return (
                    <tr key={inv.id} className="hover:bg-slate-50/70 transition-colors">
                      <td className="py-3.5 px-4 font-mono font-bold text-[#15803d]">
                        {inv.invoice_number}
                      </td>

                      <td className="py-3.5 px-4">
                        <div className="font-bold text-slate-900">{inv.customer_name}</div>
                        {inv.customer_phone && (
                          <div className="text-[11px] text-slate-400">{inv.customer_phone}</div>
                        )}
                        {inv.customer_email && (
                          <div className="text-[11px] text-slate-400">{inv.customer_email}</div>
                        )}
                      </td>

                      <td className="py-3.5 px-4 text-slate-600 whitespace-nowrap">
                        {new Date(inv.invoice_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        <div className="text-[10px] text-slate-400">
                          By: {inv.created_by_name || 'Staff'} ({inv.created_by_role || 'finance'})
                        </div>
                      </td>

                      <td className="py-3.5 px-4 max-w-xs truncate text-slate-700">
                        {inv.items && inv.items.length > 0 ? (
                          <span>
                            <strong>{inv.items.length} item{inv.items.length > 1 ? 's' : ''}:</strong> {inv.items[0].description}
                            {inv.items.length > 1 && ` +${inv.items.length - 1} more`}
                          </span>
                        ) : (
                          <span className="text-slate-400">No services</span>
                        )}
                      </td>

                      <td className="py-3.5 px-4 text-right text-slate-600">
                        ₹{Number(inv.tax_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        <div className="text-[10px] text-slate-400">({inv.tax_rate}% GST)</div>
                      </td>

                      <td className="py-3.5 px-4 text-right font-black text-slate-900 whitespace-nowrap">
                        ₹{Number(inv.total_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>

                      <td className="py-3.5 px-4 text-center">
                        <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full uppercase ${
                          isApproved ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                          isRejected ? 'bg-red-50 text-red-700 border border-red-200' :
                          'bg-amber-50 text-amber-700 border border-amber-200'
                        }`}>
                          {isApproved ? <CheckCircle className="w-3 h-3" /> :
                           isRejected ? <XCircle className="w-3 h-3" /> :
                           <Clock className="w-3 h-3" />}
                          {isApproved ? 'Approved' : isRejected ? 'Rejected' : 'Pending Approval'}
                        </span>
                      </td>

                      <td className="py-3.5 px-4 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1.5">
                          {/* View Letterhead */}
                          <button
                            onClick={() => {
                              setSelectedInvoice(inv);
                              setShowLetterheadModal(true);
                            }}
                            className="bg-emerald-50 hover:bg-emerald-100 text-emerald-700 p-1.5 rounded-lg transition-colors"
                            title="View / Print Letterhead Bill"
                          >
                            <Eye className="w-4 h-4" />
                          </button>

                          {/* Admin Quick Approval */}
                          {isAdmin && !isApproved && !isRejected && (
                            <>
                              <button
                                onClick={() => handleApprove(inv.id)}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white p-1.5 rounded-lg transition-colors"
                                title="Approve Bill"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => {
                                  setRejectingId(inv.id);
                                  setRejectionReason('');
                                }}
                                className="bg-red-50 hover:bg-red-100 text-red-600 p-1.5 rounded-lg transition-colors"
                                title="Reject Bill"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </>
                          )}

                          {/* Admin Delete */}
                          {isAdmin && (
                            <button
                              onClick={() => handleDelete(inv.id)}
                              className="text-slate-400 hover:text-red-600 p-1.5 rounded-lg transition-colors"
                              title="Delete Record"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ======================================================== */}
      {/* CREATE NEW BILL MODAL                                    */}
      {/* ======================================================== */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[92vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-700 to-[#15803d] text-white px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-black tracking-tight">Generate New Customer Bill</h3>
                <p className="text-xs text-emerald-100">
                  {isAdmin
                    ? 'Fill details, specify services, and authorize immediately or queue for approval.'
                    : 'Fill customer details and services provided. This bill will be submitted for Admin approval.'}
                </p>
              </div>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-1.5 rounded-lg text-emerald-100 hover:text-white hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <form onSubmit={handleSubmitInvoice} className="overflow-y-auto flex-1 p-6 space-y-6 text-xs">
              {/* Customer Information */}
              <div>
                <h4 className="text-xs font-extrabold text-slate-900 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-600"></span>
                  1. Customer & Invoice Info
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">
                      Customer Name *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Ramesh Sharma / TechCorp Pvt Ltd"
                      value={formData.customer_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">
                      Customer Phone / Mobile
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. +91 9876543210"
                      value={formData.customer_phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, customer_phone: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">
                      Customer Email
                    </label>
                    <input
                      type="email"
                      placeholder="customer@example.com"
                      value={formData.customer_email}
                      onChange={(e) => setFormData(prev => ({ ...prev, customer_email: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">
                      Customer GSTIN (Optional)
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. 23AAAAA0000A1Z5"
                      value={formData.customer_gstin}
                      onChange={(e) => setFormData(prev => ({ ...prev, customer_gstin: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none font-mono uppercase"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">
                      Invoice Date
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.invoice_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, invoice_date: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">
                      Payment Due Date
                    </label>
                    <input
                      type="date"
                      value={formData.due_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>

                  <div className="sm:col-span-2 lg:col-span-3">
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">
                      Billing Address
                    </label>
                    <input
                      type="text"
                      placeholder="Street, City, State, PIN code"
                      value={formData.customer_address}
                      onChange={(e) => setFormData(prev => ({ ...prev, customer_address: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Services & Line Items */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-xs font-extrabold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-600"></span>
                    2. Services Provided
                  </h4>

                  <button
                    type="button"
                    onClick={addItemRow}
                    className="text-xs font-bold text-emerald-700 hover:text-emerald-800 bg-emerald-50 px-2.5 py-1 rounded-lg border border-emerald-200 flex items-center gap-1"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Add Another Service
                  </button>
                </div>

                <div className="space-y-2.5">
                  {formItems.map((item, idx) => (
                    <div key={idx} className="p-3 bg-slate-50 border border-slate-200 rounded-xl flex flex-col sm:flex-row items-start sm:items-center gap-2">
                      <div className="flex-1 w-full">
                        <input
                          type="text"
                          required
                          placeholder="Service description (e.g. Website development sprint 1)"
                          value={item.description}
                          onChange={(e) => handleItemChange(idx, 'description', e.target.value)}
                          className="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                          list="services-list"
                        />
                      </div>

                      <div className="flex items-center gap-2 w-full sm:w-auto">
                        <div className="w-24">
                          <input
                            type="text"
                            placeholder="SAC"
                            value={item.sac_code}
                            onChange={(e) => handleItemChange(idx, 'sac_code', e.target.value)}
                            className="w-full px-2 py-1.5 border border-slate-300 rounded-lg text-center font-mono text-[11px]"
                            title="SAC Code"
                          />
                        </div>

                        <div className="w-16">
                          <input
                            type="number"
                            min="1"
                            step="any"
                            placeholder="Qty"
                            value={item.qty}
                            onChange={(e) => handleItemChange(idx, 'qty', e.target.value)}
                            className="w-full px-2 py-1.5 border border-slate-300 rounded-lg text-right font-semibold"
                            title="Quantity / Hours"
                          />
                        </div>

                        <div className="w-28">
                          <input
                            type="number"
                            min="0"
                            step="any"
                            placeholder="Rate (₹)"
                            value={item.rate}
                            onChange={(e) => handleItemChange(idx, 'rate', e.target.value)}
                            className="w-full px-2 py-1.5 border border-slate-300 rounded-lg text-right font-semibold"
                            title="Unit Rate (₹)"
                          />
                        </div>

                        <div className="w-28 text-right font-bold text-slate-900 pr-2">
                          ₹{item.amount ? item.amount.toLocaleString('en-IN') : '0'}
                        </div>

                        <button
                          type="button"
                          onClick={() => removeItemRow(idx)}
                          className="text-slate-400 hover:text-red-600 p-1"
                          title="Remove row"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}

                  <datalist id="services-list">
                    {COMMON_SERVICES.map((s, i) => (
                      <option key={i} value={s} />
                    ))}
                  </datalist>
                </div>
              </div>

              {/* Tax & Discount Configurations */}
              <div>
                <h4 className="text-xs font-extrabold text-slate-900 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-600"></span>
                  3. Tax & Calculations
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">
                      GST Type
                    </label>
                    <select
                      value={formData.tax_type}
                      onChange={(e) => {
                        const type = e.target.value;
                        let rate = 18;
                        if (type === 'exempt') rate = 0;
                        setFormData(prev => ({ ...prev, tax_type: type, tax_rate: rate }));
                      }}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    >
                      <option value="cgst_sgst_18">Intra-State GST (CGST 9% + SGST 9% = 18%)</option>
                      <option value="igst_18">Inter-State GST (IGST 18%)</option>
                      <option value="custom">Custom GST Rate</option>
                      <option value="exempt">Nil / Exempt (0%)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">
                      GST Rate (%)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="any"
                      disabled={formData.tax_type === 'exempt'}
                      value={formData.tax_rate}
                      onChange={(e) => setFormData(prev => ({ ...prev, tax_rate: Number(e.target.value) }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none disabled:bg-slate-100"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">
                      Discount (₹)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.discount}
                      onChange={(e) => setFormData(prev => ({ ...prev, discount: Number(e.target.value) }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Total Summary Breakdown */}
              <div className="p-4 bg-emerald-50/70 border border-emerald-200 rounded-2xl space-y-1.5 text-xs">
                <div className="flex justify-between text-slate-600">
                  <span>Subtotal:</span>
                  <span className="font-semibold text-slate-900">₹{subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
                {formData.discount > 0 && (
                  <div className="flex justify-between text-emerald-700">
                    <span>Discount:</span>
                    <span className="font-semibold">-₹{Number(formData.discount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                )}
                <div className="flex justify-between text-slate-600">
                  <span>GST ({formData.tax_rate}%):</span>
                  <span className="font-semibold text-slate-900">₹{taxAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="border-t border-emerald-200 pt-2 flex justify-between items-center text-sm font-black text-slate-900">
                  <span>Total Payable:</span>
                  <span className="text-base text-[#15803d]">₹{grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>

              {/* Terms / Notes */}
              <div>
                <label className="block text-[11px] font-bold text-slate-700 mb-1">
                  Terms & Payment Notes
                </label>
                <textarea
                  rows={2}
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                />
              </div>

              {/* Admin Direct Approval Option */}
              {isAdmin && (
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="direct_approve"
                    checked={formData.direct_approve}
                    onChange={(e) => setFormData(prev => ({ ...prev, direct_approve: e.target.checked }))}
                    className="w-4 h-4 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500"
                  />
                  <label htmlFor="direct_approve" className="text-xs font-bold text-slate-800 cursor-pointer">
                    Approve and Finalize immediately (Skip pending approval queue)
                  </label>
                </div>
              )}

              {/* Action Buttons */}
              <div className="pt-2 flex justify-end gap-3 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-5 py-2.5 border border-slate-300 rounded-xl font-bold text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-6 py-2.5 rounded-xl shadow-lg shadow-emerald-600/20 transition-all disabled:opacity-50"
                >
                  {submitting
                    ? 'Saving...'
                    : (isAdmin && formData.direct_approve)
                    ? 'Generate & Approve Bill'
                    : 'Submit for Admin Approval'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* REJECT MODAL (ADMIN)                                     */}
      {/* ======================================================== */}
      {rejectingId && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl space-y-4">
            <h3 className="font-bold text-slate-900 text-base">Reject Bill</h3>
            <p className="text-xs text-slate-500">
              Provide remarks or corrections required for this invoice.
            </p>
            <textarea
              rows={3}
              placeholder="e.g. Services hours need adjustment, wrong GST percentage..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              className="w-full text-xs p-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:outline-none"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setRejectingId(null)}
                className="px-4 py-2 border border-slate-300 text-xs font-bold text-slate-700 rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-xl"
              >
                Reject Bill
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* OFFICIAL LETTERHEAD PREVIEW MODAL                        */}
      {/* ======================================================== */}
      <InvoiceLetterheadModal
        invoice={selectedInvoice}
        isOpen={showLetterheadModal}
        onClose={() => setShowLetterheadModal(false)}
        isAdmin={isAdmin}
        onApprove={handleApprove}
      />
    </div>
  );
};
